import React from 'react';

import Text from './field_inputs/Text';
import Name from './field_inputs/Name';
import Number from './field_inputs/Number';
import Currency from './field_inputs/Currency';
import DisplayCurrency from './field_inputs/DisplayCurrency';
import Date from './field_inputs/Date';
import Item from './field_inputs/Item';
import ItemList from './field_inputs/ItemList';
import Select from './field_inputs/Select';
import SelectMany from './field_inputs/SelectMany';
import SelectLabel from './field_inputs/SelectLabel';
import Boolean from './field_inputs/Boolean';
import Switch from './field_inputs/Switch';
import { createGetSuggestionsFromApiUrl } from './field_inputs/HelpMethods';
import { useTranslation } from 'react-i18next';
import { ITEM_TITLE_COLLABORATORS, ITEM_TITLE_COMMITMENT, ITEM_TITLE_COMPANY, ITEM_TITLE_CONSID_CONSULTANTS, ITEM_TITLE_CONTACT_PERSON, ITEM_TITLE_CONTRACT_END, ITEM_TITLE_CONTRACT_START, ITEM_TITLE_CONTRACT_UPLOADED, ITEM_TITLE_CUSTOMER, ITEM_TITLE_DAUGHTER_COMPANY, ITEM_TITLE_EMAIL, ITEM_TITLE_FOLLOWERS, ITEM_TITLE_FRAMEWORK_AGREEMENT, ITEM_TITLE_HOURLY_RATE, ITEM_TITLE_INVOICE_ADDRESS, ITEM_TITLE_INVOICE_REFERENCE, ITEM_TITLE_LOCATION, ITEM_TITLE_LOSS_CAUSE, ITEM_TITLE_OPTION_LENGTH, ITEM_TITLE_OTHER_LOCATION, ITEM_TITLE_OTHER_ROLE, ITEM_TITLE_PARENT_COMPANY, ITEM_TITLE_PHONE, ITEM_TITLE_PUBLIC_SECTOR, ITEM_TITLE_ROLE, ITEM_TITLE_SCOPE, ITEM_TITLE_SUBCONTRACTORS, ITEM_TITLE_TECHNICAL_ENVIRONMENT, ITEM_TITLE_UC_COMPANY, ITEM_TITLE_UC_MARGIN, ITEM_TITLE_UC_PRICE_IN } from '../../../i18n/keyConstants';

/*
 *
 *   COMPONENTS
 *
 */

export const ItemDetails = ({ title, components, type }) => {
    return (
        <div className='d-flex flex-column card w-100 detail-card-info-form'>
            <h3 className='crm-header p-3 pb-2'>{title}</h3>
            <div className='crm-detail-hline' />
            <EditForm {...{ components, type }} />
        </div>
    );
};

const EditForm = ({ components, type }) => {
    return (
        <div className='edit-deal-form mb-0'>
            <div className='mx-auto'>
                {components.map((c, index) => {
                    if (index === 0 && type === 'details') return null;
                    return React.cloneElement(c.component, { key: index });
                })}
            </div>
        </div>
    );
};

/*
 *
 *   FUNCTIONS
 *
 */

/**
 * Applies initialValue from item[mapping] and handleSave(value) on React components.
 */
const applyCommonProps = (item, components, updateProps) => {
    components.forEach((c) => {
        const saveProp = async (v) => {
            const newProps = {};
            newProps[c.mapping] = v;
            return await updateProps(newProps);
        };
        c.component = React.cloneElement(c.component, {
            initialValue: item[c.mapping],
            handleSave: (v) => saveProp(v),
        });
    });
};

export const createDealFieldComponents = (deal, store, updateDealProps, t) => {
    const components = [
        {
            mapping: 'name',
            component: <Name icon='deal' />,
        },
        {
            mapping: 'customer',
            component: (
                <Item
                    label={t(ITEM_TITLE_CUSTOMER)}
                    icon='organization'
                    isCreationPermitted
                    getSuggestions={createGetSuggestionsFromApiUrl('/api/crmOrganizationFront/getOrganizationSuggestions')}
                />
            ),
        },
        {
            mapping: 'contact',
            component: (
                <Item
                    label={t(ITEM_TITLE_CONTACT_PERSON)}
                    icon='person'
                    isCreationPermitted
                    getSuggestions={createGetSuggestionsFromApiUrl('/api/crmPersonFront/getPersonSuggestions')}
                />
            ),
        },
        {
            mapping: 'hasCommitted',
            component: <Switch label={t(ITEM_TITLE_COMMITMENT)} />,
        },
        {
            mapping: 'extent',
            component: <Number label={t(ITEM_TITLE_SCOPE)} isPercentage isInt />,
        },
        {
            mapping: 'startDate',
            component: <Date label={t(ITEM_TITLE_CONTRACT_START)} />,
        },
        {
            mapping: 'endDate',
            component: <Date label={t(ITEM_TITLE_CONTRACT_END)} />,
        },
        {
            mapping: 'optionDate',
            component: <Date label={t(ITEM_TITLE_OPTION_LENGTH)} />,
        },
        {
            mapping: 'hourRate',
            component: <Number label={t(ITEM_TITLE_HOURLY_RATE)} isCurrency />,
        },
        {
            mapping: 'consultants',
            component: (
                <ItemList
                    label={t(ITEM_TITLE_CONSID_CONSULTANTS)}
                    icon='people'
                    isCreationPermitted
                    getSuggestions={createGetSuggestionsFromApiUrl('/api/crmPersonFront/getPersonSuggestions')}
                />
            ),
        },
        {
            mapping: 'underConsultantOrganizations',
            component: (
                <ItemList
                    label={t(ITEM_TITLE_UC_COMPANY)}
                    icon='organization'
                    isCreationPermitted
                    getSuggestions={createGetSuggestionsFromApiUrl('/api/crmOrganizationFront/getOrganizationSuggestions')}
                />
            ),
        },
        {
            mapping: 'underConsultants',
            component: (
                <ItemList
                    label={t(ITEM_TITLE_SUBCONTRACTORS)}
                    icon='people'
                    isCreationPermitted
                    getSuggestions={createGetSuggestionsFromApiUrl('/api/crmPersonFront/getPersonSuggestions')}
                />
            ),
        },
        {
            mapping: 'priceInRate',
            component: <Currency label={t(ITEM_TITLE_UC_PRICE_IN)} />,
        },
        {
            mapping: 'marginRate',
            component: <DisplayCurrency label={t(ITEM_TITLE_UC_MARGIN)} />,
        },
        {
            mapping: 'hasAgreement',
            component: <Switch label={t(ITEM_TITLE_CONTRACT_UPLOADED)} />,
        },
        {
            mapping: 'invoiceReference',
            component: <Text label={t(ITEM_TITLE_INVOICE_REFERENCE)} />,
        },
        {
            mapping: 'lostReason',
            component: <Select label={t(ITEM_TITLE_LOSS_CAUSE)} options={store.lostReasonOptions} />,
        },
        {
            mapping: 'collaborators',
            component: (
                <ItemList
                    label={t(ITEM_TITLE_COLLABORATORS)}
                    icon='user'
                    getSuggestions={createGetSuggestionsFromApiUrl('/api/crmUserFront/getUserSuggestions')}
                />
            ),
        },
        {
            mapping: 'followers',
            component: (
                <ItemList
                    label={t(ITEM_TITLE_FOLLOWERS)}
                    icon='user'
                    getSuggestions={createGetSuggestionsFromApiUrl('/api/crmUserFront/getUserSuggestions')}
                />
            ),
        },
    ];

    applyCommonProps(deal, components, updateDealProps);

    return components;
};

export const createOrganizationRelationComponents = (organization, store, updateOrganizationProps, t) => {
    const components = [
        {
            mapping: 'parentOrganization',
            component: (
                <Item
                    label={t(ITEM_TITLE_PARENT_COMPANY)}
                    icon='organization'
                    isCreationPermitted
                    getSuggestions={createGetSuggestionsFromApiUrl('/api/crmOrganizationFront/getOrganizationSuggestions')}
                />
            ),
        },
        {
            mapping: 'childOrganizations',
            component: (
                <ItemList
                    label={t(ITEM_TITLE_DAUGHTER_COMPANY)}
                    icon='organization'
                    isCreationPermitted
                    getSuggestions={createGetSuggestionsFromApiUrl('/api/crmOrganizationFront/getOrganizationSuggestions')}
                />
            ),
        },
    ];

    applyCommonProps(organization, components, updateOrganizationProps);

    return components;
};

export const createOrganizationFieldComponents = (organization, store, updateOrganizationProps, t) => {
    const components = [
        {
            mapping: 'name',
            component: <Name icon='organization' urlLink={organization.link} />,
        },
        {
            mapping: 'organizationType',
            component: <SelectLabel options={store.LabelOptions} />,
        },
        {
            mapping: 'places',
            component: <SelectMany label={t(ITEM_TITLE_LOCATION)} icon='place' options={store.placeOptions} />,
            /*component:(<ItemList
                    label='Ort'
                    icon='place'
                    getSuggestions={createGetSuggestionsFromApiUrl('/api/CrmSharedFront/getPlaceSuggestions')}
                />
            ),*/
        },
        {
            mapping: 'placeOther',
            component: <Text label={t(ITEM_TITLE_OTHER_LOCATION)} />,
        },
        {
            mapping: 'skills',
            component: <SelectMany label={t(ITEM_TITLE_TECHNICAL_ENVIRONMENT)} icon='place' options={store.SkillOptions} />,
        },
        {
            mapping: 'frameworkAgreement',
            component: <Boolean label={t(ITEM_TITLE_FRAMEWORK_AGREEMENT)} />,
        },
        {
            mapping: 'publicSector',
            component: <Boolean label={t(ITEM_TITLE_PUBLIC_SECTOR)} />,
        },
        {
            mapping: 'address',
            component: <Text label={t(ITEM_TITLE_INVOICE_ADDRESS)} />,
        },
    ];

    applyCommonProps(organization, components, updateOrganizationProps);

    return components;
};

export const createPersonFieldComponents = (person, store, updatePersonProps, t) => {
    const components = [
        {
            mapping: 'name',
            component: <Name icon='person' urlLink={person.link} />,
        },
        {
            mapping: 'organization',
            component: (
                <Item
                    label={t(ITEM_TITLE_COMPANY)}
                    icon='organization'
                    isCreationPermitted
                    getSuggestions={createGetSuggestionsFromApiUrl('/api/crmOrganizationFront/getOrganizationSuggestions')}
                />
            ),
        },
        {
            mapping: 'places',
            component: <SelectMany label={t(ITEM_TITLE_LOCATION)} icon='place' options={store.placeOptions} />,
        },
        {
            mapping: 'role',
            component: <Select label={t(ITEM_TITLE_ROLE)} icon='role' options={store.RoleOptions} />,
        },
        {
            mapping: 'roleOther',
            component: <Text label={t(ITEM_TITLE_OTHER_ROLE)} />,
        },
        {
            mapping: 'skills',
            component: <SelectMany label={t(ITEM_TITLE_TECHNICAL_ENVIRONMENT)} icon='skill' options={store.SkillOptions} />,
        },
        {
            mapping: 'phone',
            component: <Text label={t(ITEM_TITLE_PHONE)} />,
        },
        {
            mapping: 'email',
            component: <Text label={t(ITEM_TITLE_EMAIL)} />,
        },
    ];

    applyCommonProps(person, components, updatePersonProps);

    return components;
};
